import { useState } from 'react'
import type { CSSProperties } from 'react'
import {
  actionKindIcon,
  actionKindToken,
  useRunBookingAction,
  type BookingActionButton,
  type BookingActionCard,
} from '@/entities/booking'
import { cn } from '@/shared/lib/cn'
import { Icon, SectionCard } from '@/shared/ui'
import { ManagerReasonPrompt } from '@/features/manager-reason'
import s from './BookingActionsCard.module.css'

/**
 * Секция 2 — «Требует вашего действия» (prototype/Prototype.dc.html:2321).
 *
 * Единственная перекрашенная секция: заливка `--accent 5%`, кайма `--accent 28%`, точка и
 * счётчик акцентом. Кнопка зовёт `POST /bookings/{id}/actions/{key}`; ответ — свежая
 * `BookingDetail` уже без выполненного пункта, её кладёт в кеш `useRunBookingAction`.
 *
 * **Рисуем ровно то, что пришло с сервера.** Пусто — секции нет вовсе (см. `BookingModal`).
 *
 * ⚠️ **Карточка «Подтвердите документы» из макета здесь НЕ рисуется, и это осознанное
 * отступление** (план фазы 2, «Осознанные отступления», п. 1). В макете она вторая в списке —
 * с иконкой документа, тоном `--st-confirmed` и бейджем «не реализовано». Но вида действия
 * `docs` в контракте НЕТ (`kind` = `pay | decision | deposit | checkin`): сервер такую карточку
 * не пришлёт никогда, а нарисовать её статикой значило бы **заявить, что гость ЭТОЙ конкретной
 * брони прислал документы на проверку**. Это не заглушка будущей функции, а ложный факт о живой
 * броне — менеджер решит, что документы пришли, и пойдёт их искать. Ровно та же причина, по
 * которой в переписке не рисуется вложение «паспорт.jpg».
 */
export function BookingActionsCard({
  bookingId,
  actions,
}: {
  bookingId: string
  actions: ReadonlyArray<BookingActionCard>
}) {
  const run = useRunBookingAction(bookingId)
  // Действие не выполняется сразу: сначала спрашиваем «почему» (и сумму, если её ждут) — это
  // требование сервера, а не подтверждение «вы уверены».
  const [pending, setPending] = useState<BookingActionButton | null>(null)

  return (
    <SectionCard
      className={s.card}
      eyebrow={
        <span className={s.eyebrow}>
          <span className={s.eyebrowDot} aria-hidden="true" />
          Требует вашего действия
        </span>
      }
      aside={<span className={s.count}>{actions.length}</span>}
    >
      <div className={s.body}>
        {actions.map((action) => (
          <div key={action.id} className={s.item}>
            {/* Токен приходит инлайн-переменной — один класс плитки красится под любой вид,
                без ветвления в CSS. Тот же приём, что у `BookingStatusChip`. */}
            <span
              className={s.tile}
              style={{ '--tint': actionKindToken(action.kind) } as CSSProperties}
            >
              <Icon name={actionKindIcon(action.kind)} size={17} strokeWidth={1.9} />
            </span>

            <div className={s.itemBody}>
              <div className={s.title}>{action.title}</div>
              <div className={s.description}>{action.description}</div>

              <div className={s.buttons}>
                {action.actions.map((button) => (
                  <button
                    key={button.key}
                    type="button"
                    /*
                     * Вид — по ХАРАКТЕРУ действия с сервера (3.2): отказ не должен выглядеть
                     * обычной кнопкой. Раньше сюда приезжал выдуманный нами `style`, и «Отказать»
                     * ничем не отличалось от «Разрешить».
                     */
                    className={cn(
                      s.button,
                      button.nature === 'confirm' ? s.primary : s.secondary,
                      button.nature === 'reject' ? s.reject : undefined,
                    )}
                    /*
                     * Гаснут все кнопки секции, пока летит любое действие. Двух действий на одной
                     * броне одновременно быть не должно: каждое возвращает свежую `BookingDetail`
                     * целиком, и второй ответ затёр бы первый — гонка, исход которой зависит от
                     * порядка приземления.
                     *
                     * Состояния «заблокирована» у этих кнопок в макете нет — и не могло быть:
                     * в макете нет сервера. Вид взят у `shared/ui/icon-button` (единственной
                     * кнопки кита, у которой disabled нарисован), чтобы не выдумывать третий
                     * вариант. Дорисованное состояние здесь безопасно: оно живёт ровно столько,
                     * сколько летит запрос, и не подменяет собой вид покоя (ср. HANDOFF §5,
                     * «Выдуманное состояние, которое случается ЧАЩЕ нарисованного»).
                     *
                     * Упавшее действие показывает тост — он висит на `MutationCache`
                     * (app/providers/query-client.ts), локально ловить нечего.
                     */
                    disabled={run.isPending}
                    onClick={() => setPending(button)}
                  >
                    {button.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {pending ? (
        <ManagerReasonPrompt
          // Ключ — по действию: у каждого свой экземпляр, поэтому набранная причина не может
          // достаться следующему действию, а закрытие возвращает фокус на кнопку, что его открыла.
          key={pending.key}
          spec={{
            title: pending.label,
            confirmLabel: pending.label,
            // Обещание сдержано в той же карточке брони, секцией ниже: причина видна цитатой в
            // «Что уже делали» (BookingHistoryCard). Прежняя оговорка на этом месте — «историю
            // интерфейс пока не показывает» — устарела вместе с её появлением.
            description: 'Причина запишется в историю брони.',
            isReject: pending.nature === 'reject',
            needsAmount: pending.needsAmount,
            facts: pending.facts,
            outcomeLabel: pending.outcomeLabel,
            reasonSuggestions: pending.reasonSuggestions,
          }}
          isPending={run.isPending}
          onCancel={() => setPending(null)}
          onConfirm={async (payload) => {
            try {
              // ЖДЁМ ответ и только потом закрываем: сервер вправе отклонить вызов (нет причины,
              // нет суммы), и закрытие «на отправке» стирало бы набранное — в потоке, который весь
              // построен вокруг того, что сервер может отказать.
              await run.mutateAsync({
                actionKey: pending.key,
                // Собираем тело ЯВНО: у диалога есть поле выбора (его просит «вмешаться»), а в
                // действия оно не входит — разлитый payload дописал бы в запрос лишний ключ.
                body: {
                  reason: payload.reason,
                  ...(payload.amount === undefined ? {} : { amount: payload.amount }),
                },
              })
              setPending(null)
            } catch {
              // Ошибку показывает тост на MutationCache; ловим, чтобы не было unhandled rejection.
            }
          }}
        />
      ) : null}
    </SectionCard>
  )
}
